/* eslint-disable react-hooks/exhaustive-deps */
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  Form,
  FormDialogMode,
  FormOptions,
  RowCustomAction,
  Section,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button, Grid, makeStyles, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumCommodity,
  EnumPrecioUnitario,
  EnumTipoPosicion,
  EnumUnidadMedida,
  NewPosicionPrecio,
  NewPricePosition,
  NewTramo,
  PosicionPrecio,
  PositionType,
  TradeAditionalsDetails,
  TradeDetailsResponse,
  TradePricingDetails,
  Tramo
} from '../../../../api'
import { EnumPriceCalculationType, EnumRedondeo } from '../../../../api/Argentia/enums'
import ButtonWithPermissions from '../../../../components/buttonWithPermissions/buttonWithPermissions'
import PageHeader from '../../../../components/page/pageHeader'
import { PriceExpressionValidator } from '../../../../components/shared'
import { enumEnumToValueNumber, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { missingFieldsForm } from '../../../../helpers/validateFormHelper'
import { getActiveBalanceZone, getCustomerDomain, notifyError } from '../../../../redux'
import {
  RulebookValidations,
  SelectOptions,
  TradeFormValidations
} from '../../edit/tradesEdit.types'
import {
  checkDates,
  getErrorText,
  getNewPriceDates,
  getPropsToUpdate,
  getTotalQuantity,
  positionSimpleSameDates
} from '../../helpers/trades.helper'
import {
  currenciesMapperToDropDown,
  formatDataForPositionTable,
  mapPositionSimpleToForm
} from '../../trades.mapper'
import {
  checkDeletePositionRulebook,
  checkDeleteTractRulebook,
  checkLogElementRulebook,
  checkPositionTypeRulebook,
  checkPresentFutureDatesRulebook,
  checkQuantityRulebook,
  createPositionPrice,
  createPriceForPosition,
  editPositionPrice,
  fetchDeliveryPoints,
  generatePositionCode,
  validatePositionRulebook
} from '../../trades.service'
import { styles } from './positionForm.styles'

import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useErrorDetailListDialog,
  useFormDialog
} from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'

import { ErrorDetailList } from '../../../../components/errorDetailList/errorDetailList'
import { getGasDay } from '../../../../helpers/gasDayHelper'
import { formatStringNumber } from '../../../../helpers/numberHelper'
import { getUserPermissions, hasPermission } from '../../../../helpers/permissionsHelper'
import { Pages, Resources } from '../../../../model/RBAC/constants'
import {
  FormMode,
  FormModeType,
  initialValidations,
  quantityDecimals
} from '../tradeForm/tradeForm'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'

export type DatesInterval = {
  startDate?: Date
  endDate?: Date
}
export interface TradesEditPageProps {
  tradeMode: FormModeType
  positionMode: FormModeType
  trade: TradeDetailsResponse | null
  currency?: number
  tradeDatesInterval?: DatesInterval
  counterpart?: number
  position?: NewPosicionPrecio
  price?: TradePricingDetails
  isSimple?: boolean
  deletable?: boolean
  onRefreshTrade?: () => void
  onGoBack: () => void
  onAddPositionToTable?: (position: PosicionPrecio) => void
  onEditPositionTable?: (position: PosicionPrecio) => void
  onDeletePosition: (position: NewPosicionPrecio) => Promise<boolean>
  initialDependencies?: SelectOptions
}

const emptyDependencies = {
  unitPrices: [],
  counterparts: [],
  balanceZones: [],
  deliveryPoints: [],
  tradeTypes: [],
  books: [],
  contractedProducts: [],
  brokers: [],
  currencies: [],
  currenciesOptions: [],
  measureUnitsOptions: [],
  contractOptions: []
}

const initialNewPosition = {
  tramos: [{} as NewTramo]
}

export const PositionForm = ({
  tradeMode,
  positionMode,
  trade,
  tradeDatesInterval,
  currency,
  counterpart,
  position,
  price,
  isSimple,
  deletable = false,
  onRefreshTrade,
  onGoBack,
  onAddPositionToTable,
  onEditPositionTable,
  onDeletePosition,
  initialDependencies
}: TradesEditPageProps) => {
  const [t] = useTranslation(['trades', 'tradesEdit', 'globalerisLibrary', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()
  const activeBalanceZone = getActiveBalanceZone()
  const editPermission = hasPermission(Resources.Trades, [UserPermission.EDIT])
  const initialShowTractsTable =
    positionMode === FormMode.CREATE
      ? false
      : position
        ? !positionSimpleSameDates(position, position?.tramos)
        : false

  //Starts Inputs
  const [formDependencies, setFormDependencies] = useState<SelectOptions>(
    initialDependencies ? initialDependencies : emptyDependencies
  )
  // Position Form
  const [formStatePosition, setFormStatePosition] = useState<Partial<NewPosicionPrecio>>(
    positionMode === FormMode.EDIT && position
      ? mapPositionSimpleToForm(position)
      : { ...initialNewPosition, zonaBalance: activeBalanceZone?.id }
  )

  // Tracts
  const [tractsList, setTractsList] = useState<(Tramo | NewTramo)[]>(
    positionMode === FormMode.EDIT && position?.tramos ? position.tramos : []
  )
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Partial<Tramo>>({})
  const [validQuantity, setValidQuantity] = useState<number | undefined>(undefined)
  const [validTractStartDate, setValidTractStartDate] = useState<Date | undefined>(undefined)
  const [validTractEndDate, setValidTractEndDate] = useState<Date | undefined>(undefined)

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  //   const [showAddPositionForm, setShowAddPositionForm] = useState(false);
  const [quantitySuffix, setQuantitySuffix] = useState<string>('')
  const [pastTract, setPastTract] = useState<boolean>(false)
  const [tractStartDateDisabled, setTractStartDateDisabled] = useState<boolean>(false)
  const [tractEndDateDisabled, setTractEndDateDisabled] = useState<boolean>(false)

  // PastPosition is meant to be used only as a result of checkDates.
  const [pastPosition, setPastPosition] = useState<boolean>(false)
  const [startDateDisabled, setStartDateDisabled] = useState<boolean>(false)
  const [endDateDisabled, setEndDateDisabled] = useState<boolean>(false)

  const [validations, setValidations] = useState<TradeFormValidations>(initialValidations)
  const [validInitDate, setValidInitDate] = useState<Date | undefined>(undefined)
  const [validEndDate, setValidEndDate] = useState<Date | undefined>(undefined)
  const [validMeasureUnit, setValidMeasureUnit] = useState<number | undefined>(undefined)
  const [startDateMsgError, setStartDateMsgError] = useState<string | undefined>(undefined)
  const [endDateMsgError, setEndDateMsgError] = useState<string | undefined>(undefined)
  const [quantityMsgError, setQuantityMsgError] = useState<string | undefined>(undefined)

  const [deliveryPointEditable, setDeliveryPointEditable] = useState<boolean>(true)
  const [billingLevelDisabled, setBillingLevelDisabled] = useState<boolean>(true)
  const [billingCycleDisabled, setBillingCycleDisabled] = useState<boolean>(true)
  const [contractDisabled, setContractDisabled] = useState<boolean>(true)
  const [showTractsTable, setShowTractsTable] = useState<boolean>(initialShowTractsTable)

  const [startDateTractMsgError, setStartDateTractMsgError] = useState<string | undefined>(
    undefined
  )
  const [endDateTractMsgError, setEndDateTractMsgError] = useState<string | undefined>(undefined)
  const [quantityTractMsgError, setQuantityTractMsgError] = useState<string | undefined>(undefined)
  //Ends Inputs

  //Starts Pricing
  const [expressionValidated, setExpressionValidated] = useState<boolean>(true)
  const [resetPriceExpressionForm, setResetPriceExpressionForm] = useState(false)
  //Ends Pricing

  //Starts General Page States
  const [pendingChanges, setPendingChanges] = useState<boolean>(false)
  const [requiredFields, setRequiredFields] = useState<boolean>(false)
  const [formStateAditionals, setFormStateAditionals] = useState<
    Partial<TradeAditionalsDetails> | undefined
  >(undefined)

  const currencyCodString = currency
    ? formDependencies.currencies.find(x => x.id === currency)?.codCurrency
    : ''

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  //Ends General Page States

  const handleDiscard = () => {
    onGoBack()
  }

  const onSave = async (formData: Partial<NewPosicionPrecio>) => {
    if (tractsList && tractsList.length == 0 && !showTractsTable) {
      notifyError(t('tradesEdit:forms.sections.positions.actions.save.errors.noTracts'))
      return false
    }

    if ((formData && missingFieldsForm(positionSections, formData)) || formData === null) {
      notifyError(t('trades:forms.add.notifications.error.errorInvalidForm'))
      return false
    }

    const isValidPositionStrategy = counterpart
      ? await validatePositionRulebook(
          formData as PosicionPrecio,
          trade?.tradeId ?? 0,
          counterpart,
          {
            startDate: formData.fechaInicio,
            endDate: formData.fechaFin
          },
          false,
          showTractsTable
        ).then(response => {
          if (!response.success) {
            showErrorDetailListDialog(
              t('tradesEdit:forms.errors.rulebook'),
              response.error
                ? [{ code: response.error.code, message: `${response.error.message}` }]
                : [{ code: '412', message: '8001' }],
              t(`rulebook:titles.8001`)
            )
          }
          return response.success
        })
      : true

    if (!isValidPositionStrategy) return

    if (tradeMode === FormMode.EDIT) {
      // Save changes on DDBB
      return onSavePosition(formData as PosicionPrecio)
    } else if (tradeMode === FormMode.CREATE && positionMode === FormMode.CREATE) {
      return onAddPosition(formData as PosicionPrecio)
    } else if (tradeMode === FormMode.CREATE && positionMode === FormMode.EDIT) {
      return savePositionOnCreateTrade(formData as PosicionPrecio)
    }
    return onAddPosition(formData as PosicionPrecio)
  }

  const handleDelete = async (position: NewPosicionPrecio): Promise<boolean> => {
    if (tradeMode === FormMode.EDIT && positionMode === FormMode.EDIT && counterpart) {
      const canDelete = await checkDeletePositionRulebook(position.idPosicion!, counterpart).then(
        response => {
          if (!response.success) {
            showErrorDetailListDialog(
              t('tradesEdit:forms.errors.rulebook'),
              response.error
                ? [{ code: response.error.code, message: `${response.error.message}` }]
                : [{ code: '412', message: '8001' }],
              t(`rulebook:titles.8001`)
            )
          }
          return response.success
        }
      )
      if (!canDelete) return false
    }

    const response = await onDeletePosition(position)
    if (response) {
      tradeMode === FormMode.EDIT && onRefreshTrade && onRefreshTrade()
      onGoBack()
    }
    return response
  }

  //#region Handlers

  //#endregion

  const handleFormDirty = (validation: RulebookValidations, valid: boolean, cause?: string) => {
    setValidations(prevValidations => {
      const newValidation = { ...prevValidations, [validation]: valid }
      const isDirty = !(
        newValidation.initDate &&
        newValidation.endDate &&
        newValidation.counterpart &&
        newValidation.quantity &&
        newValidation.deliveryPoint &&
        newValidation.signDate &&
        newValidation.positionType &&
        newValidation.measureUnit
      )
      return { ...newValidation, isDirty: isDirty }
    })

    if (validation === RulebookValidations.INIT_DATE) {
      cause && !valid ? setStartDateMsgError(cause) : setStartDateMsgError(undefined)
    } else if (validation === RulebookValidations.END_DATE) {
      cause && !valid ? setEndDateMsgError(cause) : setEndDateMsgError(undefined)
    } else if (validation === RulebookValidations.QUANTITY) {
      cause && !valid ? setQuantityMsgError(cause) : setQuantityMsgError(undefined)
    }
  }

  const restoreErrors = () => {
    setValidations(initialValidations)
    setStartDateMsgError(undefined)
    setEndDateMsgError(undefined)
  }

  const setFilterCurrencies = async (measureUnitId: any) => {
    const unitPricesByMeasure = formDependencies.unitPrices.filter(
      x => x.measureUnit === measureUnitId
    )
    const currenciesIdsFiltered = unitPricesByMeasure.flatMap(x => x.currency)
    let currenciesFiltered = formDependencies.currencies.filter(x =>
      currenciesIdsFiltered.includes(x.id)
    )
    if (measureUnitId === null) {
      currenciesFiltered = formDependencies.currencies
    }
    setFormDependencies(prevDep => ({
      ...prevDep,
      currenciesOptions: currenciesMapperToDropDown(currenciesFiltered)
    }))
  }

  const checkDeliveryPoint = (deliveryPointId: number, deliveryPointDesc: string) => {
    const isDeliveryPointInList =
      formDependencies.deliveryPoints &&
      formDependencies.deliveryPoints.find(dp => dp.value === deliveryPointId)
    if (!isDeliveryPointInList) {
      setFormDependencies(prevDep => ({
        ...prevDep,
        deliveryPoints: [
          ...formDependencies.deliveryPoints,
          {
            value: deliveryPointId,
            label: deliveryPointDesc
          }
        ]
      }))
      setDeliveryPointEditable(false)
    }
  }

  const updateDeliveryPoints = async (zone: number) => {
    await fetchDeliveryPoints(zone).then(response => {
      setFormDependencies(prevDep => ({
        ...prevDep,
        deliveryPoints: response
      }))
    })
  }

  const onAddPosition = async (formData: PosicionPrecio) => {
    // Method used to add the new position to the to-be-created trade (tradeMode = CREATE)
    if (formData === undefined) {
      onGoBack()
      return false
    }

    if (formData) {
      if (counterpart === undefined) {
        // Counterpart input cannot be cleared and is set by default with the 1st element from api response. This error will be caused if subject API is down.
        notifyError(t('trades:forms.positionForm.notifications.error.missingCounterpart'))
        return false
      }

      await generatePositionCode({ positionType: formData.tipoPosicion }).then(response => {
        const priceData: NewPricePosition | null =
          formData.price &&
          formData.price.priceCalculationType &&
          formData.price.expression &&
          formData.price.priceUnit &&
          formData.price.rounding
            ? {
                priceCalculationType: formData!.price!.priceCalculationType!,
                expression: formData.price.expression || '',
                priceUnit: formData.price.priceUnit!,
                rounding: formData.price.rounding!,
                version: 1
              }
            : null

        if (priceData && !expressionValidated) {
          notifyError(t('trades:forms.add.notifications.error.errorExpression'))
        }

        const formatPosition = formatDataForPositionTable(
          formData,
          formDependencies.deliveryPoints,
          priceData,
          response?.data?.code,
          tractsList
        )
        onAddPositionToTable && onAddPositionToTable(formatPosition)
      })

      onGoBack()
      return true
    } else {
      return false
    }
  }

  const onSavePosition = async (formData: PosicionPrecio) => {
    if (formData === undefined) {
      onGoBack()
      setFormStatePosition({
        zonaBalance: activeBalanceZone?.id
      } as PosicionPrecio)

      return false
    }

    if (formData) {
      if (position === undefined || position.idPosicion === undefined) {
        // Create Position
        // We generate a new PositionCode and then PATCH the trade to create the new position+price.
        const priceData: NewPricePosition | null =
          formData.price &&
          formData.price.priceCalculationType &&
          formData.price.expression &&
          formData.price.priceUnit &&
          formData.price.rounding
            ? {
                priceCalculationType: formData.price.priceCalculationType,
                expression: formData.price.expression ?? '',
                priceUnit: formData.price.priceUnit,
                rounding: formData.price.rounding,
                version: 1
              }
            : null

        if (priceData && !expressionValidated) {
          notifyError(t('trades:forms.add.notifications.error.errorExpression'))
        }

        const positionToCreate: PosicionPrecio = formatDataForPositionTable(
          formData,
          formDependencies.deliveryPoints,
          priceData,
          undefined,
          tractsList
        )
        const response = await createPositionPrice(trade!.tradeId, positionToCreate)

        if (!response.success) {
          notifyError('Error al intentar crear la posición.')
          return false
        } else {
          info(t('tradesEdit:notifications.save.success'))
          onGoBack()
          setFormStatePosition({
            zonaBalance: activeBalanceZone?.id
          } as PosicionPrecio)
          onRefreshTrade && onRefreshTrade()
          onGoBack()
          return true
        }
      } else if (formData.idPosicion && trade) {
        // Edit Position
        let refreshPage = false

        const propsToUpdate = getPropsToUpdate(formData)

        const { priceStartDate, priceEndDate } = getNewPriceDates(
          {
            newPositionStartDate: propsToUpdate.fechaInicio,
            newPositionEndDate: propsToUpdate.fechaFin
          },
          formData.tramos
        )

        if (!position.price && formData.price) {
          expressionValidated &&
            formData.price.priceUnit &&
            formData.price.priceCalculationType &&
            formData.price.rounding &&
            formData.price.expression &&
            (await createPriceForPosition(trade.tradeId, position.idPosicion, {
              expression: formData.price.expression,
              priceCalculationType: formData.price.priceCalculationType,
              priceUnit: formData.price.priceUnit,
              rounding: formData.price.rounding,
              startDate: priceStartDate ? moment(priceStartDate).format('YYYY-MM-DD') : undefined,
              endDate: priceEndDate ? moment(priceEndDate).format('YYYY-MM-DD') : undefined
            }).then(response => {
              if (response.success) {
                refreshPage = true
                setPendingChanges(false)
              } else {
                notifyError(t('tradesEdit:notifications.save.createPriceError'))
              }
            }))
        }

        propsToUpdate &&
          (await editPositionPrice(trade.tradeId, formData, propsToUpdate).then(response => {
            if (response && response.success) {
              refreshPage = true

              setFormStatePosition({
                zonaBalance: activeBalanceZone?.id
              } as PosicionPrecio)
            } else {
              notifyError(t('tradesEdit:notifications.save.updatePositionError'))
              return false
            }
          }))

        if (refreshPage) {
          info(t('tradesEdit:notifications.save.success'))
          onRefreshTrade && onRefreshTrade()
          onGoBack()
          return true
        }
      }
    } else {
      return false
    }
  }

  const savePositionOnCreateTrade = async (formData: PosicionPrecio) => {
    if (formData === undefined) {
      // Cancel
      onGoBack()
      return false
    }
    //We generate a new PositionCode and then PATCH the trade to create the new position+price.
    if (formData) {
      if (counterpart === undefined) {
        // Counterpart input cannot be cleared and is set by default with the 1st element from api response. This error will be caused if subject API is down.
        notifyError(t('trades:forms.positionForm.notifications.error.missingCounterpart'))
        return false
      }

      const priceData: NewPricePosition | null =
        formData.price &&
        formData.price.priceCalculationType &&
        formData.price.expression &&
        formData.price.priceUnit &&
        formData.price.rounding
          ? {
              priceCalculationType: formData!.price!.priceCalculationType!,
              expression: formData.price.expression || '',
              priceUnit: formData.price.priceUnit!,
              rounding: formData.price.rounding!,
              version: 1
            }
          : null

      if (priceData && !expressionValidated) {
        notifyError(t('trades:forms.add.notifications.error.errorExpression'))
      }

      const auxFormData = { ...formData, tramos: tractsList }
      const formatDeliveryPoint = formDependencies.deliveryPoints.find(
        deliveryPoint => deliveryPoint.value === formData.puntoEntregaId
      )
      const position = enumToSelectOptionArrayDefaultValues(PositionType, 'enums:PositionType', [
        PositionType.Sale,
        PositionType.Purchase
      ])
      const formatPosition = position.find(pos => pos.value === formData.tipoPosicion)
      onEditPositionTable &&
        onEditPositionTable({
          ...auxFormData,
          puntoEntrega: {
            id: formData.puntoEntregaId,
            descripcion: formatDeliveryPoint?.label
          },
          tipoPosicion: formatPosition?.label || ''
        })
      onGoBack()
      return true
    } else {
      return false
    }
  }

  const setQuantityUnit = (measureUnitID: number) => {
    const measureUnit =
      formDependencies.measureUnitsOptions &&
      formDependencies.measureUnitsOptions.find(opt => opt.value === measureUnitID)
    if (measureUnit) {
      setQuantitySuffix(measureUnit.label)
    } else {
      setQuantitySuffix('')
    }
  }

  //#region Form definition

  const positionSections: Section[] = [
    {
      title: t('tradesEdit:forms.sections.positions.formTitle'),
      inputs: [
        {
          name: 'descripcion',
          label: t('tradesEdit:forms.sections.trade.inputs.description'),
          type: DataType.TEXT,
          onChange: newDesc => {
            setPendingChanges(true)
            setFormStatePosition(prevState => ({ ...prevState, descripcion: String(newDesc) }))
          },
          readOnly: !editPermission
        },
        {
          name: 'tipoPosicion',
          label: t('tradesEdit:forms.sections.trade.inputs.type'),
          type: DataType.SELECT,
          inputSize: 3,
          selectOptions: enumToSelectOptionArrayDefaultValues(PositionType, 'enums:PositionType', [
            PositionType.Sale,
            PositionType.Purchase
          ]),
          readOnly: !(editPermission && !pastPosition),
          required: true,
          errorState: !validations.positionType,
          errorText: !validations.positionType ? t('tradesEdit:forms.errors.positionType') : '',
          onChange: async newType => {
            setFormStatePosition(prevState => ({ ...prevState, tipoPosicion: newType }))
            if (enumEnumToValueNumber(EnumTipoPosicion, position?.tipoPosicion) === +newType) {
              handleFormDirty(RulebookValidations.POSITION_TYPE, true)
              return
            }
            if (positionMode === FormMode.EDIT) {
              trade &&
                position?.idPosicion &&
                counterpart &&
                (await checkPositionTypeRulebook(
                  trade.tradeId,
                  position?.idPosicion,
                  counterpart
                ).then(async response => {
                  if (response.success) {
                    // if Rulebook validates, then we update the form:
                    setPendingChanges(true)
                    handleFormDirty(RulebookValidations.POSITION_TYPE, true)
                  } else if (response.error) {
                    handleFormDirty(RulebookValidations.POSITION_TYPE, false)
                  } else {
                    // error de api
                  }
                }))
            } else setPendingChanges(true)
          }
        },
        {
          name: 'zonaBalance',
          label: t('tradesEdit:forms.sections.trade.inputs.balanceZone'),
          type: DataType.SELECT,
          selectOptions: formDependencies.balanceZones,
          required: true,
          onChange: async (value: any) => {
            // Every time Balance Zone is changed, we need to re-fetch the delivery Points for that zone.
            setFormStatePosition(prevState => ({ ...prevState, zonaBalance: value }))
            updateDeliveryPoints(value)
            setPendingChanges(true)
          },
          readOnly: !(editPermission &&
          !pastPosition &&
          formDependencies.balanceZones &&
          formDependencies.balanceZones.length > 1
            ? true
            : false)
        },
        {
          name: 'puntoEntregaId',
          label: t('tradesEdit:forms.sections.trade.inputs.deliveryPoint'),
          type: DataType.SELECT,
          selectOptions: formDependencies.deliveryPoints,
          required: true,
          readOnly: !(editPermission && !pastPosition),
          errorState: !validations.deliveryPoint,
          errorText: !validations.deliveryPoint ? t('tradesEdit:forms.errors.logElement') : '',
          onChange: async newDeliveryPointId => {
            setFormStatePosition(prevState => ({
              ...prevState,
              puntoEntrega: { id: +newDeliveryPointId }
            }))
            if (
              position &&
              position.puntoEntrega &&
              newDeliveryPointId === position.puntoEntrega.id
            ) {
              handleFormDirty(RulebookValidations.DELIVERY_POINT, true)
              return
            }
            if (
              positionMode === FormMode.EDIT &&
              position &&
              position.puntoEntrega &&
              position.puntoEntrega.id
            ) {
              trade &&
                position?.idPosicion &&
                counterpart &&
                (await checkLogElementRulebook(
                  trade.tradeId,
                  position?.idPosicion,
                  +newDeliveryPointId,
                  position.puntoEntrega.id
                ).then(async response => {
                  if (response.success) {
                    // if Rulebook validates, then we update the form:
                    setPendingChanges(true)
                    handleFormDirty(RulebookValidations.DELIVERY_POINT, true)
                  } else if (response.error) {
                    handleFormDirty(RulebookValidations.DELIVERY_POINT, false)
                  } else {
                    // error de api
                  }
                }))
            } else setPendingChanges(true)
          }
        },
        {
          name: 'unidadMedida',
          label: t('tradesEdit:forms.sections.trade.inputs.dailyQuantityUnit'),
          type: DataType.SELECT,
          selectOptions: formDependencies.measureUnitsOptions,
          required: true,
          readOnly: !(editPermission && !pastPosition),
          errorState: !validations.measureUnit,
          errorText: !validations.measureUnit ? t('tradesEdit:forms.errors.measureUnit') : '',
          onChange: async (newMeasureUnitID: any, formState: any) => {
            setFilterCurrencies(newMeasureUnitID)
            setQuantityUnit(newMeasureUnitID)
            //setPriceUnit(newMeasureUnitID)
            setFormStatePosition(prevState => ({ ...prevState, unidadMedida: newMeasureUnitID }))
            if (newMeasureUnitID === validMeasureUnit) {
              handleFormDirty(RulebookValidations.MEASURE_UNIT, true)
              return
            }
            if (
              tradeMode === FormMode.EDIT &&
              positionMode === FormMode.EDIT &&
              position &&
              position.tramos.length === 1 &&
              position.tramos[0].cantidadDiaria &&
              counterpart &&
              formStatePosition.fechaInicio &&
              formStatePosition.cantidadDiaria
            ) {
              await checkQuantityRulebook(
                position!.idPosicion!,
                counterpart,
                formStatePosition.fechaInicio,
                position.tramos[0].cantidadDiaria * 2,
                formStatePosition?.fechaFin,
                position.tramos[0].cantidadDiaria
              ).then(async response => {
                if (response.success) {
                  // if Rulebook validates, then we update the form:
                  setPendingChanges(true)
                  setValidMeasureUnit(newMeasureUnitID)
                  handleFormDirty(RulebookValidations.MEASURE_UNIT, true)
                } else if (response.error) {
                  handleFormDirty(RulebookValidations.MEASURE_UNIT, false)
                } else {
                  // error de api
                }
              })
            } else {
              setValidMeasureUnit(newMeasureUnitID)
              setPendingChanges(true)
            }
          }
        },
        {
          name: 'fechaInicio',
          label: t('tradesEdit:forms.sections.trade.inputs.initDate'),
          type: DataType.DATE,
          errorState: !validations.initDate,
          errorText:
            !validations.initDate && startDateMsgError ? getErrorText(startDateMsgError) : '',
          required: true,
          readOnly: !(editPermission && !startDateDisabled),
          onBlur: async (newStartDate: any) => {
            const endDate = formStatePosition.fechaFin
            let tracts: NewTramo[] = []

            setFormStatePosition(prevState => ({
              ...prevState,
              fechaInicio: moment(newStartDate).toDate()
            }))

            if (
              formStatePosition.tramos &&
              formStatePosition.tramos.length === 1 &&
              tractsList &&
              !showTractsTable
            ) {
              tracts = [{ ...tractsList[0], fechaInicio: moment(newStartDate).toDate() }]
              setTractsList(tracts => [
                { ...tracts[0], fechaInicio: moment(newStartDate).toDate() }
              ])
            }

            if (moment(newStartDate).isSame(moment(position?.fechaInicio), 'days')) {
              handleFormDirty(RulebookValidations.INIT_DATE, true)
              return
            }

            if (endDate && moment(endDate).isBefore(moment(newStartDate), 'days')) {
              handleFormDirty(RulebookValidations.INIT_DATE, false, 'positionStartDate')
              return
            } else {
              handleFormDirty(RulebookValidations.INIT_DATE, true)
              endDateMsgError === 'positionEndDate' &&
                handleFormDirty(RulebookValidations.END_DATE, true)
              setValidInitDate(newStartDate)
              setPendingChanges(true)
            }
          }
        },
        {
          name: 'fechaFin',
          label: t('tradesEdit:forms.sections.trade.inputs.endDate'),
          type: DataType.DATE,
          required: true,
          errorState: !validations.endDate,
          errorText: !validations.endDate && endDateMsgError ? getErrorText(endDateMsgError) : '',
          readOnly: !(
            editPermission &&
            (formStatePosition && positionMode === FormMode.EDIT
              ? !(
                  endDateDisabled &&
                  formStatePosition.fechaFin &&
                  moment(formStatePosition.fechaFin).isBefore(getGasDay())
                )
              : true)
          ),
          onBlur: async newEndDate => {
            const startDate = formStatePosition.fechaInicio
            setFormStatePosition(prevState => ({
              ...prevState,
              fechaFin: moment(newEndDate).toDate()
            }))

            if (
              formStatePosition.tramos &&
              formStatePosition.tramos.length === 1 &&
              tractsList &&
              !showTractsTable
            ) {
              setTractsList(tracts => [{ ...tracts[0], fechaFin: moment(newEndDate).toDate() }])
            }

            if (moment(newEndDate).isSame(moment(position?.fechaFin), 'days')) {
              handleFormDirty(RulebookValidations.END_DATE, true)
              return
            }

            if (startDate && moment(newEndDate).isBefore(moment(startDate), 'days')) {
              handleFormDirty(RulebookValidations.END_DATE, false, 'positionEndDate')
              return
            } else {
              startDateMsgError === 'positionStartDate' &&
                handleFormDirty(RulebookValidations.INIT_DATE, true)
              handleFormDirty(RulebookValidations.END_DATE, true)
              setValidEndDate(moment(newEndDate).toDate())
              setPendingChanges(true)
            }
          }
        },
        {
          // Positions with one tract/segment, will show dailyQuantity from the tract.
          name: 'cantidadDiaria',
          required: true,
          display: !showTractsTable,
          label: t('tradesEdit:forms.sections.trade.inputs.dailyQuantity'),
          type: DataType.NUMBER,
          customDecimalScale: quantityDecimals,
          suffix: quantitySuffix,
          readOnly: !(editPermission && !pastPosition),
          errorState: !validations.quantity,
          errorText:
            !validations.quantity && quantityMsgError ? getErrorText(quantityMsgError) : '',
          onBlur: async newQuantity => {
            let tracts: NewTramo[] = []
            if (
              formStatePosition.tramos &&
              formStatePosition.tramos.length === 1 &&
              tractsList &&
              !showTractsTable
            ) {
              tracts = [
                { ...tractsList[0], cantidadDiaria: formatStringNumber(newQuantity as string) }
              ]
              setTractsList(tracts => [
                { ...tracts[0], cantidadDiaria: formatStringNumber(newQuantity as string) }
              ])
              setFormStatePosition(prevState => ({
                ...prevState,
                cantidadDiaria: formatStringNumber(newQuantity as string)
              }))
            }

            if (tradeMode === FormMode.EDIT && positionMode === FormMode.EDIT) {
              if (!showTractsTable) {
                const response =
                  tracts.length > 0 &&
                  (await checkPresentFutureDatesRulebook(
                    trade?.tradeId!,
                    trade?.positions[0].idPosicion!,
                    counterpart!,
                    tracts!
                  ).then(response => {
                    if (!response.success) {
                      handleFormDirty(
                        RulebookValidations.QUANTITY,
                        false,
                        t('tradesEdit:forms.errors.quantityFormalizedOperations')
                      )
                    } else {
                      handleFormDirty(RulebookValidations.QUANTITY, true)
                      setPendingChanges(true)
                    }
                    return response.success
                  }))
              } else {
                setPendingChanges(true)
                handleFormDirty(RulebookValidations.QUANTITY, true)
              }
            } else {
              setPendingChanges(true)
            }
          }
        },
        {
          // Cantidad Total: corresponde a la posición (suma de las cantidades de todos los tramos).
          name: 'cantidadTotal',
          label: t('tradesEdit:forms.sections.trade.inputs.totalQuantity'),
          type: DataType.NUMBER,
          customDecimalScale: quantityDecimals,
          readOnly: true,
          suffix: quantitySuffix
        },
        {
          // Positions with one tract/segment, will show fee from the tract.
          name: 'fee',
          label: t('tradesEdit:forms.sections.trade.inputs.fee'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.DEFAULT,
          suffix: currencyCodString,
          display: !showTractsTable,
          onChange: newFee => {
            if (
              formStatePosition.tramos &&
              formStatePosition.tramos.length === 1 &&
              tractsList &&
              !showTractsTable
            ) {
              setTractsList(tracts => [{ ...tracts[0], fee: +newFee }])
            }
            setFormStatePosition({
              ...formStatePosition,
              fee: +newFee
            })
            setPendingChanges(true)
          },
          readOnly: !editPermission
        }
      ]
    }
  ]

  const optionsPosition: FormOptions = {
    elevation: 1,
    columns: 4,
    /*  onDelete: async formData => { return handleDelete(formData) },
    // saveDisabled: !pendingChanges,
    saveDisabled: !editPermission,
    deleteDisabled: !editPermission && !deletable, */
    analytics: {
      customer: getCustomerDomain(),
      view: t('trades:forms.add.analytics')
    }
  }

  const doResetPriceExpressionForm = () => {
    setResetPriceExpressionForm(true)
    setResetPriceExpressionForm(false)
  }

  //#tracts switch
  const onChangeTractsSwitch = () => {
    if (pendingChanges) {
      getConfirmation(ConfirmationDialogs.SHOW_TRACTS).then(async () => {
        setShowTractsTable(prevValue => !prevValue)
        setPendingChanges(false)

        if (positionMode === FormMode.EDIT) {
          if (position) {
            setFormStatePosition(mapPositionSimpleToForm(position))
            doResetPriceExpressionForm()
            setTractsList(position.tramos)
            if (position?.unidadMedida) {
              const measureUnit = parseInt(EnumUnidadMedida[position.unidadMedida])
              //setPriceUnit(measureUnit)
            }
          }
        } else {
          setFormStatePosition(prevValue => {
            return {
              ...initialNewPosition,
              zonaBalance: activeBalanceZone?.id,
              price: { priceCalculationType: 1 }
            }
          })
          doResetPriceExpressionForm()
          setTractsList([])
          setQuantitySuffix('')
        }

        closeConfirmation()
      })
    } else {
      setShowTractsTable(prevValue => !prevValue)
    }
  }
  //#tracts table

  const getTotalQuantityInTracts = (tracts: (NewTramo | Tramo)[]) => {
    let totalQuantity = 0
    tracts.forEach(tract => {
      totalQuantity += getTotalQuantity({
        initDate: tract.fechaInicio,
        endDate: tract.fechaFin,
        dailyQuantity: tract.cantidadDiaria
      })
    })
    return totalQuantity
  }

  const getTractIndex = row => {
    const index = tractsList.findIndex(tract => {
      return (
        tract.cantidadDiaria === row.cantidadDiaria &&
        tract.fee === row.fee &&
        tract.fechaFin === row.fechaFin &&
        tract.fechaInicio === row.fechaInicio
      )
    })
    return index
  }

  const editFormDefinition = useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
    return {
      id: 'EDIT',
      sections: [
        {
          inputs: [
            {
              name: 'fechaInicio',
              label: t('tradesEdit:forms.sections.trade.inputs.initDate'),
              type: DataType.DATE,
              required: true,
              readOnly: !(
                editPermission &&
                (tradeMode === FormMode.EDIT && positionMode === FormMode.EDIT
                  ? !(
                      tractStartDateDisabled &&
                      selectedRowToEdit.fechaInicio &&
                      moment(selectedRowToEdit.fechaInicio).isBefore(getGasDay())
                    )
                  : true)
              ),
              errorState: startDateTractMsgError !== undefined,
              errorText: startDateTractMsgError ? getErrorText(startDateTractMsgError) : '',
              onBlur: async (newStartDate: any) => {
                if (!moment(newStartDate).isValid()) {
                  return
                }
                if (
                  positionMode === FormMode.EDIT &&
                  position?.fechaInicio &&
                  moment(newStartDate).isSame(moment(position?.fechaInicio), 'days')
                ) {
                  setStartDateTractMsgError(undefined)
                  setSelectedRowToEdit(prevState => ({ ...prevState, fechaInicio: newStartDate }))
                  return
                }

                if (
                  selectedRowToEdit.fechaFin &&
                  moment(selectedRowToEdit.fechaFin).isBefore(moment(newStartDate), 'days')
                ) {
                  setStartDateTractMsgError('positionStartDate')
                } else {
                  endDateTractMsgError === 'positionEndDate' && setEndDateTractMsgError(undefined)
                  startDateTractMsgError === 'positionStartDate' &&
                    setStartDateTractMsgError(undefined)
                }
                setSelectedRowToEdit(prevState => ({ ...prevState, fechaInicio: newStartDate }))
              }
            },
            {
              name: 'fechaFin',
              label: t('tradesEdit:forms.sections.trade.inputs.endDate'),
              type: DataType.DATE,
              required: true,
              readOnly: !(
                editPermission &&
                (tradeMode === FormMode.EDIT && formStatePosition && positionMode === FormMode.EDIT
                  ? !(
                      tractEndDateDisabled &&
                      selectedRowToEdit.fechaFin &&
                      moment(selectedRowToEdit.fechaFin).isBefore(getGasDay())
                    )
                  : true)
              ),
              errorState: endDateTractMsgError !== undefined,
              errorText: endDateTractMsgError ? getErrorText(endDateTractMsgError) : '',
              onBlur: async (newEndDate: any) => {
                if (!moment(newEndDate).isValid()) {
                  return
                }
                if (
                  positionMode === FormMode.EDIT &&
                  position?.fechaFin &&
                  moment(newEndDate).isSame(moment(position?.fechaFin), 'days')
                ) {
                  setEndDateTractMsgError(undefined)
                  setSelectedRowToEdit(prevState => ({ ...prevState, fechaFin: newEndDate }))
                  return
                }
                if (
                  selectedRowToEdit.fechaInicio &&
                  moment(selectedRowToEdit.fechaInicio).isAfter(moment(newEndDate), 'days')
                ) {
                  setEndDateTractMsgError('positionEndDate')
                } else {
                  endDateTractMsgError === 'positionEndDate' && setEndDateTractMsgError(undefined)
                  startDateTractMsgError === 'positionStartDate' &&
                    setStartDateTractMsgError(undefined)
                  setValidTractEndDate(newEndDate)
                }
                setSelectedRowToEdit(prevState => ({ ...prevState, fechaFin: newEndDate }))
              }
            },
            {
              name: 'cantidadDiaria',
              label: t('tradesEdit:forms.sections.trade.inputs.dailyQuantity'),
              type: DataType.NUMBER,
              customDecimalScale: quantityDecimals,
              readOnly: pastTract && tradeMode === FormMode.EDIT && positionMode === FormMode.EDIT,
              suffix: quantitySuffix,
              required: true,
              errorState: quantityTractMsgError !== undefined,
              errorText: quantityTractMsgError ? getErrorText(quantityTractMsgError) : ''
              // onBlur: async newQuantity => {
              //   setSelectedRowToEdit(prevState => ({
              //     ...prevState,
              //     cantidadDiaria: parseGroupedFloat(String(newQuantity))
              //   }))
              //   setPendingChanges(true)
              //   setFormStatePosition({
              //     ...formStatePosition,
              //     cantidadDiaria: parseGroupedFloat(String(newQuantity))
              //   })
              // }
            },
            {
              name: 'fee',
              label: t('tradesEdit:forms.sections.trade.inputs.fee'),
              type: DataType.NUMBER,
              decimalScale: DecimalScale.DEFAULT,
              suffix: currencyCodString
            }
          ]
        }
      ]
    }
  }, [
    selectedRowToEdit,
    formStatePosition,
    currencyCodString,
    tractStartDateDisabled,
    tractEndDateDisabled,
    pastTract,
    startDateTractMsgError,
    endDateTractMsgError,
    quantityTractMsgError,
    validTractStartDate,
    validTractEndDate
  ])

  const onEditValueClickHandler = async (tract: Tramo) => {
    const index = getTractIndex(tract)
    setSelectedRowToEdit(tract)
    setValidQuantity(tract.cantidadDiaria)
    setValidTractStartDate(tract.fechaInicio)
    setValidTractEndDate(tract.fechaFin)

    // validateTractDates(tract.fechaInicio, tract.fechaFin).then(res => setFormWaiting(false))

    openForm({
      definition: editFormDefinition,
      title: t('tradesEdit:forms.sections.positions.editTract'),
      mode: FormDialogMode.EDIT,
      isWaiting: true,
      onClose: formData => onCloseEditForm(formData, index, tract)
    })
  }

  const onAddValueClickHandler = () => {
    setSelectedRowToEdit({})

    openForm({
      definition: editFormDefinition,
      title: t('tradesEdit:forms.sections.positions.addTract'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => onCloseAddForm(formData)
    })
  }

  const onRowDelete = async (tract: Tramo) => {
    const isValidRulebook =
      tradeMode === FormMode.EDIT && positionMode === FormMode.EDIT
        ? await checkDeleteTractRulebook(
            trade?.tradeId!,
            position!.idPosicion!,
            tract.fechaInicio,
            tract.fechaFin,
            trade?.company,
            counterpart
          ).then(response => {
            if (!response.success) {
              showErrorDetailListDialog(
                t('tradesEdit:forms.errors.rulebook'),
                response.error
                  ? [{ code: response.error.code, message: response.error.message }]
                  : [{ code: '412', message: '8001' }],
                t(`rulebook:titles.8001`)
              )
            }
            return response.success
          })
        : true
    if (!isValidRulebook) {
      return
    }
    getConfirmation(ConfirmationDialogs.DELETE_TRACT).then(async () => {
      setPendingChanges(true)
      const auxTableData: any = [...tractsList]
      const index = getTractIndex(tract)
      auxTableData.splice(index, 1)
      setTractsList(auxTableData)
      closeConfirmation()
    })
  }

  const onCloseAddForm = async (newTract: NewTramo) => {
    if (newTract) {
      setPendingChanges(true)
      if (
        (!formStatePosition?.fechaInicio || !formStatePosition?.fechaFin) &&
        tractsList.length < 1
      ) {
        setFormStatePosition(prevState => {
          return {
            ...prevState,
            fechaInicio: formStatePosition.fechaInicio || newTract.fechaInicio,
            fechaFin: formStatePosition.fechaFin || newTract.fechaFin
          }
        })
      }

      setTractsList([...tractsList, newTract])
    }
    setValidTractStartDate(undefined)
    setValidTractEndDate(undefined)
    setValidQuantity(undefined)
    closeForm()
  }

  const onCloseEditForm = async (tract: Tramo, index, originalRow) => {
    if (tract) {
      setPendingChanges(true)
      const auxTableData = [...tractsList]
      auxTableData[index] = { ...auxTableData[index], ...tract }
      setTractsList(auxTableData)
    }
    setValidTractStartDate(undefined)
    setValidTractEndDate(undefined)
    setValidQuantity(undefined)
    setStartDateTractMsgError(undefined)
    setEndDateTractMsgError(undefined)
    setQuantityTractMsgError(undefined)
    closeForm()
  }

  const isPriceIncompleteOrInvalid = (): boolean => {
    if (
      expressionValidated &&
      formStatePosition.price?.priceUnit &&
      formStatePosition.price.priceCalculationType &&
      formStatePosition.price?.rounding &&
      formStatePosition.price?.expression
    ) {
      return false
    } else if (
      (typeof formStatePosition.price?.priceUnit === 'undefined' &&
        typeof formStatePosition.price?.rounding === 'undefined' &&
        typeof formStatePosition.price?.expression === 'undefined') ||
      formStatePosition.price?.expression === ''
    ) {
      return false
    } else if (
      (typeof formStatePosition.price?.priceUnit === 'undefined' &&
        typeof formStatePosition.price?.rounding === 'undefined' &&
        typeof formStatePosition.price?.expression === 'undefined') ||
      formStatePosition.price?.expression === ''
    ) {
      return false
    } else {
      return true
    }
  }

  const columns: DataTableColumn[] = [
    {
      name: 'fechaInicio',
      label: t('tradesEdit:forms.sections.trade.inputs.initDate'),
      type: DataType.DATE,
      required: true
    },
    {
      name: 'fechaFin',
      label: t('tradesEdit:forms.sections.trade.inputs.endDate'),
      type: DataType.DATE,
      required: true
    },
    {
      name: 'cantidadDiaria',
      label: quantitySuffix
        ? t('tradesEdit:forms.sections.positions.tractTableColumns.dailyQuantity', {
            unit: quantitySuffix
          })
        : t('tradesEdit:forms.sections.trade.inputs.dailyQuantity'),
      type: DataType.NUMBER,
      customDecimalScale: 12,
      required: true
    },
    {
      name: 'fee',
      label: currencyCodString
        ? t('tradesEdit:forms.sections.positions.tractTableColumns.fee', {
            currency: currencyCodString
          })
        : t('tradesEdit:forms.sections.trade.inputs.fee'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.DEFAULT
    }
  ]

  const getTractsTableCustomActions = (): RowCustomAction[] | undefined => {
    let customActionsList = [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('trades:forms.add.sections.commissions.edit'),
        onClick: onEditValueClickHandler
      }
    ]

    if (tractsList && tractsList.length > 1) {
      customActionsList.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('trades:forms.add.sections.commissions.delete'),
        onClick: onRowDelete
      })
    }

    return editPermission ? customActionsList : []
  }

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 1,
    canInsert: editPermission,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: editPermission,
    sortOrder: { name: 'fechaInicio', direction: SortDirection.ASC },
    onRowInsert: async (row: any) => {
      onAddValueClickHandler()
      return true
    },
    rowCustomActions: getTractsTableCustomActions()
  }
  const onClickSave = () => {
    let formData: Partial<NewPosicionPrecio>
    if (
      tractsList.length === 0 &&
      !showTractsTable &&
      formStatePosition.fechaInicio &&
      formStatePosition.fechaFin &&
      formStatePosition.cantidadDiaria
    ) {
      formData = {
        ...formStatePosition,
        tramos: [
          {
            fechaInicio: formStatePosition.fechaInicio,
            fechaFin: formStatePosition.fechaFin,
            cantidadDiaria: formStatePosition.cantidadDiaria,
            fee: formStatePosition.fee
          }
        ]
      }
    } else {
      formData = { ...formStatePosition, tramos: tractsList }
    }
    onSave(formData)
  }

  const validateDates = async (startDate: Date, endDate: Date) => {
    if (startDate && endDate && counterpart) {
      const checkResponse = await checkDates(startDate, endDate, counterpart)
      checkResponse.propsNeedToBeDisabled && checkResponse.startDateIsPast
        ? setStartDateDisabled(true)
        : setStartDateDisabled(false)
      checkResponse.propsNeedToBeDisabled && checkResponse.endDateIsPast
        ? setEndDateDisabled(true)
        : setEndDateDisabled(false)
      if (checkResponse.propsNeedToBeDisabled) {
        setPastPosition(true)
      }
    }
  }

  const validateTractDates = async (startDate: Date, endDate: Date) => {
    if (startDate && endDate && counterpart) {
      const checkResponse = await checkDates(startDate, endDate, counterpart)
      checkResponse.propsNeedToBeDisabled && checkResponse.startDateIsPast
        ? setTractStartDateDisabled(true)
        : setTractStartDateDisabled(false)
      checkResponse.propsNeedToBeDisabled && checkResponse.endDateIsPast
        ? setTractEndDateDisabled(true)
        : setTractEndDateDisabled(false)
      if (checkResponse.propsNeedToBeDisabled) {
        setPastTract(true)
      }
    }
  }
  //#endregion

  // Effects Region
  useEffect(() => {
    positionMode === FormMode.CREATE &&
      activeBalanceZone &&
      updateDeliveryPoints(activeBalanceZone?.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const state = formStatePosition ? { ...formStatePosition, tramos: tractsList } : null
    if (state) {
      const missingFields = missingFieldsForm(positionSections, state)
      setRequiredFields(!missingFields)
    }
  }, [formStatePosition])

  useEffect(() => {
    formStatePosition?.zonaBalance && updateDeliveryPoints(formStatePosition?.zonaBalance)
  }, [formStatePosition?.zonaBalance])

  useEffect(() => {
    setTimeout(() => {
      setFormStatePosition(prevState => {
        return {
          ...prevState,
          cantidadTotal: getTotalQuantityInTracts(tractsList)
        }
      })
    }, 500)
  }, [tractsList])

  useEffect(() => {
    updateFormDefinition(editFormDefinition, selectedRowToEdit)
  }, [
    editFormDefinition,
    startDateTractMsgError,
    endDateTractMsgError,
    quantityTractMsgError,
    selectedRowToEdit,
    validTractStartDate,
    validTractEndDate
  ])

  useEffect(() => {
    if (position) {
      position &&
        position?.fechaInicio &&
        counterpart &&
        positionMode === FormMode.EDIT &&
        validateDates(position?.fechaInicio, position?.fechaFin)
    }
  }, [position])

  useEffect(() => {
    if (position?.unidadMedida) {
      const measureUnit = parseInt(EnumUnidadMedida[position.unidadMedida])
      //setPriceUnit(measureUnit)
      setQuantityUnit(measureUnit)
      setValidMeasureUnit(measureUnit)
    }
  }, [])

  return (
    <>
      <CustomFormDialog {...formProps}></CustomFormDialog>
      <PageHeader
        title={t(
          `tradesEdit:forms.sections.positions.${
            positionMode === FormMode.EDIT ? 'editTitle' : 'addTitle'
          }`,
          {
            positionCode: positionMode === FormMode.EDIT ? formStatePosition.codPosicion : undefined
          }
        )}
      >
        <Button onClick={handleDiscard} size="small" color="primary">
          {t('tradesEdit:mainActions.cancel')}
        </Button>
        <Button
          onClick={onChangeTractsSwitch}
          size="small"
          color="primary"
          disabled={
            (position && position?.tramos?.length > 1) ||
            (position &&
              position?.tramos?.length === 1 &&
              !positionSimpleSameDates(position, tractsList))
          }
        >
          {showTractsTable
            ? t('trades:forms.add.mainActions.goToSimpleView')
            : t('trades:forms.add.mainActions.goToComplexView')}
        </Button>
        {positionMode === FormMode.EDIT && (
          <ButtonWithPermissions
            hasPermission={editPermission}
            onClick={() => handleDelete(formStatePosition as NewPosicionPrecio)}
            size="small"
            color="primary"
            disabled={!(editPermission && !pastPosition)}
          >
            {t('tradesEdit:mainActions.delete')}
          </ButtonWithPermissions>
        )}
        <ButtonWithPermissions
          hasPermission={editPermission}
          onClick={onClickSave}
          variant="contained"
          size="small"
          color="primary"
          disabled={
            !(
              requiredFields &&
              pendingChanges &&
              !validations.isDirty &&
              !isPriceIncompleteOrInvalid()
            )
          }
        >
          {t('tradesEdit:mainActions.save')}
        </ButtonWithPermissions>
      </PageHeader>
      <div className={classes.additionalFormStyle}>
        <Form sections={positionSections} data={formStatePosition} options={optionsPosition} />
      </div>
      <PriceExpressionValidator
        title={t('tradesEdit:forms.sections.price.title')}
        resetForm={resetPriceExpressionForm}
        readOnly={!editPermission}
        showPriceUnit
        showVersion
        showRounding
        priceType={EnumPriceType.Formula}
        commodity={EnumCommodity.GasNatural}
        clearablePriceUnit
        clearableRounding
        activeExpressionIsValid={formStatePosition.price?.expression ? true : undefined}
        activeExpression={formStatePosition.price?.expression}
        activePriceCalculationType={enumEnumToValueNumber(
          EnumPriceCalculationType,
          formStatePosition.price?.priceCalculationType
        )}
        activeRounding={enumEnumToValueNumber(EnumRedondeo, formStatePosition.price?.rounding)}
        activePriceUnit={enumEnumToValueNumber(
          EnumPrecioUnitario,
          formStatePosition.price?.priceUnit
        )}
        onIsValid={isValid => {
          setExpressionValidated(isValid)
        }}
        onSelectValidExpression={expression => {
          expression && setPendingChanges(true)
          setFormStatePosition({
            ...formStatePosition,
            price: { ...formStatePosition.price, expression: expression }
          })
        }}
        onSelectPriceCalculationType={newPriceCalculationType => {
          setFormStatePosition({
            ...formStatePosition,
            price: { ...formStatePosition.price, priceCalculationType: newPriceCalculationType }
          })
          if (
            formStatePosition?.price?.priceCalculationType &&
            formStatePosition?.price?.priceCalculationType !== newPriceCalculationType &&
            enumEnumToValueNumber(
              EnumPriceCalculationType,
              formStatePosition?.price?.priceCalculationType
            ) !== newPriceCalculationType
          ) {
            setPendingChanges(true)
          }
        }}
        onSelectPriceUnit={newPriceUnit => {
          setPendingChanges(true)
          setFormStatePosition({
            ...formStatePosition,
            price: { ...formStatePosition.price, priceUnit: newPriceUnit }
          })
        }}
        onSelectRounding={newRounding => {
          setPendingChanges(true)
          setFormStatePosition({
            ...formStatePosition,
            price: { ...formStatePosition.price, rounding: newRounding }
          })
        }}
        priceVersion={1}
      />
      {showTractsTable && (
        <Grid item xs={12}>
          <DataTable
            data={tractsList}
            columns={columns}
            options={tableOptions}
            userPermissions={getUserPermissions(Pages.Trades)}
          />
        </Grid>
      )}
      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </>
  )
}
